import { observer } from 'mobx-react';
import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LFEChart } from '@app/_components/lf-graph/LFEChart';
import { SankeySeriesOption } from 'echarts';
import { displayWeight, isPresent } from '@farmai-shared/LFUtils';
import { thousandSeparator } from '@services/utils';
import { RootStoreContext } from '@stores/rootStore';
import { MockSankey } from './mocks/MockSankey';
import { SankeyResponse } from '@services/responseTypes';

// sankey nodes can organize data via a depth property and have a specific emphasis for a specific depth

export const TraceabilitySankey: FC<{
  data?: SankeyResponse;
  legendColors: {
    [k: string]: string;
  };
  mock?: boolean;
}> = observer(({
  data,
  legendColors,
  mock = false
}) => {
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const weightUnit = userStore.user.settings.weight_unit;
  if (!isPresent(data?.links) || !isPresent(data?.links)) {
    return <MockSankey title={t('noTraceabilitySankeyData')} />;
  }
  const labelFormatter = params => t(params.name, {
    defaultValue: params.name
  });
  const tooltipFormatter = p => {
    if (p.dataType === 'node') {
      return `${p.name}: ${thousandSeparator(displayWeight(p.value, {
        minDec: 3
      }))} ${t(weightUnit)}`;
    }
    return `${t(p.data.source)} -> ${p.data.target}: ${thousandSeparator(displayWeight(parseFloat(p.data.value), {
      minDec: 3
    }))} ${t(weightUnit)}`;
  };
  return <LFEChart tooltip={{
    show: !mock,
    trigger: 'item',
    triggerOn: 'mousemove',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    className: 'traceability-tooltip',
    textStyle: {
      color: 'rgb(255,255,255)',
      fontFamily: 'Kanit'
    },
    formatter: tooltipFormatter
  }} series={[{
    type: 'sankey',
    cursor: mock ? 'default' : 'pointer',
    draggable: !mock,
    silent: mock,
    data: data.nodes.map(n => ({
      name: n.name,
      depth: n.depth,
      value: parseFloat(n.value),
      itemStyle: {
        color: legendColors[n.name]
      }
    })),
    links: data.links,
    label: {
      fontFamily: 'Kanit',
      color: '#1C1F28',
      formatter: labelFormatter,
      width: 240,
      overflow: 'truncate'
    },
    emphasis: {
      disabled: !mock,
      focus: 'adjacency'
    },
    nodeAlign: 'left',
    lineStyle: {
      color: 'gradient',
      curveness: 0.5
    }
  }] as SankeySeriesOption} />;
});